import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Modal,
  Link as MuiLink,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderIcon from "@mui/icons-material/Folder";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import DashboardLayout from "../layouts/DashboardLayout";
import PageHeader from "../components/layout/PageHeader";
import axios from "../config/axiosConfig";
import ENV from "../config/env";
import { splitTituloContenido, esVideoNoticia, esImagenNoticia, extraerYoutubeId, textoParaMostrar, extraerPlayStore } from "../utils/noticiasHelpers";
import YoutubeEmbed from "../components/news/YoutubeEmbed";
import PlayStoreCard from "../components/news/PlayStoreCard";
import { useAuth } from "../context/AuthContext";
import RegisterNewsForm from "../components/forms/RegisterNewsForm";

const firstChar = (s) => (s ? s.charAt(0).toUpperCase() : "?");

const normalize = (value) =>
  String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const NoticiaDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = useMemo(() => {
    const role = normalize(user?.role);
    return role === "admin" || role === "gerente";
  }, [user?.role]);

  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openEditar, setOpenEditar] = useState(false);

  const fetchNoticia = () => {
    setLoading(true);
    setError("");
    return axios
      .get(`/api/news/${id}`)
      .then(({ data }) => {
        setNoticia(data);
      })
      .catch((e) => {
        console.error("Error cargando noticia:", e);
        setError("No se pudo cargar la noticia.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchNoticia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { titulo, cuerpo } = noticia ? splitTituloContenido(noticia) : { titulo: "", cuerpo: "" };

  const handleEditSuccess = () => {
    setOpenEditar(false);
    fetchNoticia();
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la noticia "${titulo || "sin título"}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`/api/news/${id}`);
      navigate("/noticias");
    } catch (err) {
      console.error("Error al eliminar la noticia:", err);
      setError("No se pudo eliminar la noticia.");
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={titulo || "Detalle de noticia"}
        subtitle="Noticias"
        actions={
          canManage && noticia && (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => setOpenEditar(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton size="small" onClick={handleDelete}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        }
      />

      <Box sx={{ maxWidth: 760, mx: "auto", p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {!loading && !error && noticia && (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  src={noticia.profile_picture ? `${ENV.API_BASE_URL}${noticia.profile_picture}` : undefined}
                  sx={{ width: 32, height: 32 }}
                >
                  {firstChar(noticia.usuario)}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {noticia.usuario || "Usuario"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(noticia.fecha).format("DD/MM/YYYY")}
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" label={noticia.sucursal || "—"} />
              </Stack>

              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 3 }}>
                {textoParaMostrar(cuerpo)}
              </Typography>

              <YoutubeEmbed videoId={extraerYoutubeId(cuerpo)} sx={{ mb: 3 }} />

              {extraerPlayStore(cuerpo) && <PlayStoreCard {...extraerPlayStore(cuerpo)} sx={{ mb: 3 }} />}

              {esVideoNoticia(noticia.archivo_url) && (
                <Box
                  component="video"
                  src={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                  controls
                  preload="metadata"
                  playsInline
                  sx={{ display: "block", width: "100%", maxHeight: "70vh", bgcolor: "common.black", borderRadius: 1, mb: 3 }}
                />
              )}

              {esImagenNoticia(noticia.archivo_url) && (
                <Tooltip title="Ver en tamaño original">
                  <Box
                    component="a"
                    href={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "block", mb: 3, borderRadius: 1, overflow: "hidden", bgcolor: "action.hover", cursor: "zoom-in" }}
                  >
                    <Box
                      component="img"
                      src={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                      alt={titulo || "Imagen de la noticia"}
                      sx={{ display: "block", width: "100%", maxHeight: "70vh", objectFit: "contain" }}
                    />
                  </Box>
                </Tooltip>
              )}

              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {noticia.archivo_url && !esVideoNoticia(noticia.archivo_url) && !esImagenNoticia(noticia.archivo_url) && (
                  <Button
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    component={MuiLink}
                    href={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Descargar archivo adjunto
                  </Button>
                )}
                {noticia.documento_carpeta_id && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FolderIcon />}
                    component={RouterLink}
                    to={`/documentos/${noticia.documento_carpeta_id}`}
                  >
                    Ver documentos: {noticia.documento_carpeta_nombre || "carpeta"}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      <Modal open={openEditar} onClose={() => setOpenEditar(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 480, maxWidth: "90vw", bgcolor: "background.paper", p: 4, borderRadius: 2, maxHeight: "90vh", overflowY: "auto" }}>
          <RegisterNewsForm
            onSuccess={handleEditSuccess}
            noticia={noticia ? { ...noticia, titulo, contenido: cuerpo } : null}
          />
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default NoticiaDetallePage;
